import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import {
  Text,
  TwButton as Button,
  Loader,
  Input,
  TwDropdown as Dropdown,
  Icon,
  Popover,
  Textarea,
  TwFileUpload as FileUpload,
} from '@ui-component-library';
import type { FileItem } from '@ui-component-library/components/FileUpload/FileUpload.types';
import { DynamicModal } from '@components/DynamicModal';
import { buttonVariants } from '@ui-component-library/components/Button/Button';
import { cn } from '@utils/cn';
import { apiFetch } from '../../../../api/client';
import { fetchUserAdData } from '../../../../api/services/user';
import { useUserDataStore } from '../../store/UserDataStore';
import { useNavigationStore } from '../../store/NavigationStore';
import type {
  AccountData,
  AdListingApiPayload,
  BidsBreakdown,
  DropdownOption,
  ListingAd,
} from './myAdsListingsHelpers';
import {
  applyCategoryFilter,
  applySearchFilter,
  applyYearChange,
  buildCategoryOptions,
  normalizeAccountData,
} from './myAdsListingsHelpers';

/**
 * TwDropdown considers `''` an invalid selection (see handleSelect → valid = selectedValue !== '').
 * Use a sentinel for “egna annonser”, same pattern as year filter `all`.
 */
const CHILD_ACCOUNT_OWN = 'own';

/** Full catalog; `bad_invoice` is only offered when the user is the winning bid owner for that ad. */
const HELP_WITH_AD_TOPIC_OPTIONS_ALL: DropdownOption[] = [
  { label: 'Annonsen stämmer inte', value: 'bad_ad_data' },
  { label: 'Faktura stämmer inte', value: 'bad_invoice' },
  { label: 'Det saknas information', value: 'needs_more_data' },
  { label: 'Övrig fråga', value: 'other' },
];

function helpWithAdTopicOptionsForUser(isWinningBidOwner: boolean): DropdownOption[] {
  return HELP_WITH_AD_TOPIC_OPTIONS_ALL.filter(
    (opt) => opt.value !== 'bad_invoice' || isWinningBidOwner
  );
}

/** Short hints shown under the topic dropdown; replace when real categories exist. */
const HELP_WITH_AD_TOPIC_HINTS: Record<string, string> = {
  bad_ad_data:
    'Ange tydligt vad som inte stämmer och om du har korrekt information eller bilagor fyll i dom',
  bad_invoice:
    'Ange en kort beskrivning om vad som är fel och ladda upp mätkvitto som en bilaga om det är volymen som inte stämmer',
    needs_more_data: 'Ange tydligt vilken information som saknas',
    other: 'Ange tydligt vad du behöver hjälp med och bifoga de bilagor som kan hjälpa oss besvara frågan',
};

const emptyBids = (): BidsBreakdown => ({
  on_going: [],
  won: [],
  not_sold: [],
  lost: [],
});

function cloneAccount(a: AccountData): AccountData {
  return {
    ads: [...a.ads],
    opened: [...a.opened],
    bids: {
      on_going: [...a.bids.on_going],
      won: [...a.bids.won],
      not_sold: [...a.bids.not_sold],
      lost: [...a.bids.lost],
    },
  };
}

function getWindowUserData(): { expiringAds?: string[]; loggedInUserID?: number } | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { user_data?: { expiringAds?: string[]; loggedInUserID?: number } })
    .user_data;
}

function adUrlDetails(id: number, btn: 'ad' | 'bid'): string {
  if (btn === 'bid') {
    return `/auctions/${id}/#show_bids`;
  }
  return `/auctions/${id}/`;
}

export default function MyAdsSettings(): React.ReactElement {
  const setView = useNavigationStore((s) => s.setView);

  const initialPayloadRef = useRef<AdListingApiPayload | null>(null);
  const [bootstrap, setBootstrap] = useState<AdListingApiPayload | null>(null);
  const [account, setAccount] = useState<AccountData | null>(null);
  const accountRef = useRef<AccountData | null>(null);
  accountRef.current = account;
  const [ads, setAds] = useState<ListingAd[]>([]);
  const [bids, setBids] = useState<BidsBreakdown>(emptyBids);
  const [opened, setOpened] = useState<ListingAd[]>([]);
  const [filterYear, setFilterYear] = useState('all');
  const [filterAds, setFilterAds] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  /** `CHILD_ACCOUNT_OWN` = egna annonser; annars vald barnkontos id-sträng. */
  const [selectedChildId, setSelectedChildId] = useState(CHILD_ACCOUNT_OWN);
  const [helpWithAdModalAdId, setHelpWithAdModalAdId] = useState<number | null>(null);
  const [helpWithAdTopic, setHelpWithAdTopic] = useState(
    () => helpWithAdTopicOptionsForUser(false)[0]?.value ?? ''
  );
  const [helpWithAdDescription, setHelpWithAdDescription] = useState('');
  const [helpWithAdFiles, setHelpWithAdFiles] = useState<FileItem[]>([]);
  const [helpWithAdSubmitting, setHelpWithAdSubmitting] = useState(false);
  const [helpWithAdSubmitError, setHelpWithAdSubmitError] = useState<string | null>(null);
  const [helpWithAdSubmitSuccess, setHelpWithAdSubmitSuccess] = useState(false);

  const helpWithAdTopicDropdownOptions = useMemo(() => {
    if (helpWithAdModalAdId === null || !account) {
      return helpWithAdTopicOptionsForUser(false);
    }
    const isWinningBidOwner = account.bids.won.some((ad) => ad.id === helpWithAdModalAdId);
    return helpWithAdTopicOptionsForUser(isWinningBidOwner);
  }, [helpWithAdModalAdId, account]);

  useEffect(() => {
    if (helpWithAdModalAdId === null) return;
    const acc = accountRef.current;
    if (!acc) return;
    const isWinningBidOwner = acc.bids.won.some((ad) => ad.id === helpWithAdModalAdId);
    const opts = helpWithAdTopicOptionsForUser(isWinningBidOwner);
    setHelpWithAdTopic(opts[0]?.value ?? '');
    setHelpWithAdDescription('');
    setHelpWithAdFiles([]);
    setHelpWithAdSubmitError(null);
    setHelpWithAdSubmitSuccess(false);
  }, [helpWithAdModalAdId]);

  async function submitHelpWithAdRequest(): Promise<void> {
    if (helpWithAdModalAdId === null) return;
    setHelpWithAdSubmitting(true);
    setHelpWithAdSubmitError(null);

    const fd = new FormData();
    fd.append('ad_id', String(helpWithAdModalAdId));
    fd.append('topic', helpWithAdTopic);
    fd.append('description', helpWithAdDescription);
    helpWithAdFiles.forEach((item) => {
      if (item.file) fd.append('attachments', item.file);
    });

    const res = await apiFetch('/user-api/help-with-ad/', {
      method: 'POST',
      body: fd,
      headers: {},
    });
    setHelpWithAdSubmitting(false);
    if (!res.ok) {
      setHelpWithAdSubmitError(
        typeof res.message === 'string' ? res.message : typeof res.error === 'string' ? res.error : 'Något gick fel'
      );
      return;
    }
    setHelpWithAdSubmitSuccess(true);
  }

  function hydrateListingState(payload: AdListingApiPayload) {
    const normalizedAds = normalizeAccountData(payload.ads);
    const payloadNormalized: AdListingApiPayload = { ...payload, ads: normalizedAds };
    setBootstrap(payloadNormalized);
    const next = cloneAccount(normalizedAds);
    setAccount(next);
    setAds([...next.ads]);
    setBids(cloneAccount(next).bids);
    setOpened([...next.opened]);
  }

  function resetFromPayload(
    payload: AdListingApiPayload,
    options?: { rememberInitial?: boolean; resetChildSelection?: boolean }
  ) {
    if (options?.rememberInitial && !initialPayloadRef.current) {
      initialPayloadRef.current = payload;
    }
    hydrateListingState(payload);
    setFilterYear('all');
    setFilterAds('all');
    setSearchText('');
    if (options?.resetChildSelection) {
      setSelectedChildId(CHILD_ACCOUNT_OWN);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const store = useUserDataStore.getState().userData as Record<string, unknown> | undefined;
      if (store && store.ads && typeof store.ads === 'object') {
        resetFromPayload(store as unknown as AdListingApiPayload, {
          resetChildSelection: true,
          rememberInitial: true,
        });
        return;
      }
      const resp = await fetchUserAdData();
      if (!cancelled && resp.ok && resp.data) {
        resetFromPayload(resp.data as AdListingApiPayload, {
          resetChildSelection: true,
          rememberInitial: true,
        });
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap from store or API
  }, []);

  async function fetchChildAccounts(childIds: string[]) {
    if (childIds.length === 0) {
      const initial = initialPayloadRef.current;
      if (initial) {
        resetFromPayload(initial);
      }
      return;
    }
    if (!bootstrap) return;
    setLoading(true);
    try {
      const resp = await apiFetch('/my-account/listings/childaccounts/', {
        method: 'POST',
        body: JSON.stringify({
          child_ids: childIds.map((id) => Number.parseInt(id, 10)).filter((n) => !Number.isNaN(n)),
        }),
        headers: { 'Content-Type': 'application/json' },
      });
      if (!resp.ok || !resp.data || typeof resp.data !== 'object') {
        window.alert('Oops! Något gick fel...');
        return;
      }
      const data = resp.data as {
        ads: unknown;
        years?: Array<number | string>;
      };
      const merged: AdListingApiPayload = {
        ads: data.ads as AccountData,
        years: data.years ?? bootstrap.years,
        child_accounts: bootstrap.child_accounts,
      };
      hydrateListingState(merged);
    } finally {
      setLoading(false);
    }
  }

  function handleSearchChange(value: string) {
    setSearchText(value);
    if (!account) return;
    const next = applySearchFilter(account, value);
    setAds(next.ads);
    setBids(next.bids);
    setOpened(next.opened);
  }

  function handleYearChange(value: string, _isValid?: boolean, _name?: string) {
    if (!account) return;
    const next = applyYearChange(account, value);
    setFilterYear(next.filter_year);
    setFilterAds(next.filter_ads);
    setAds(next.ads);
    setBids(next.bids);
    setOpened(next.opened);
  }

  function handleCategoryChange(value: string, _isValid?: boolean, _name?: string) {
    if (!account) return;
    const { ads: nextAds } = applyCategoryFilter(account, filterYear, value);
    setFilterAds(value);
    setAds(nextAds);
  }

  function handleChildAccountChange(value: string, _isValid?: boolean, _name?: string) {
    setSelectedChildId(value);
    void fetchChildAccounts(value === CHILD_ACCOUNT_OWN ? [] : [value]);
  }

  function openSellTimberEditor(adId: number, e: React.MouseEvent) {
    e.preventDefault();
    const uid = getWindowUserData()?.loggedInUserID;
    if (uid) {
      Cookies.set(`ad_id_${uid}`, String(adId));
    }
    setView({ comp: 'sell-timber', sellTimberAdId: adId });
  }

  if (!account || !bootstrap) {
    return (
      <div className="w-full max-w-[700px] mx-auto">
        <section className="p-4">
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <Loader />
            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Laddar dina annonser...
            </Text>
          </div>
        </section>
      </div>
    );
  }

  const userExpiringAds = getWindowUserData()?.expiringAds ?? [];
  const hasChildAccounts = Boolean(
    bootstrap.child_accounts && Object.keys(bootstrap.child_accounts).length > 0
  );

  const yearOptions: DropdownOption[] = [
    { label: 'Visa alla', value: 'all' },
    ...(bootstrap.years ?? []).map((y) => ({ label: String(y), value: String(y) })),
  ];

  const categoryOptions = buildCategoryOptions(account, filterYear);

  const childAccountEntries: { id: string; label: string }[] = bootstrap.child_accounts
    ? (Object.entries(bootstrap.child_accounts) as [string, string][]).map(([id, label]) => ({
        id,
        label,
      }))
    : [];

  const childAccountOptions: DropdownOption[] = [
    { label: 'Egna annonser', value: CHILD_ACCOUNT_OWN },
    ...childAccountEntries.map(({ id, label }) => ({ label, value: id })),
  ];

  const filterColumnCount =
    2 + (account.ads.length > 0 ? 1 : 0) + (hasChildAccounts ? 1 : 0);
  const gridCols =
    filterColumnCount >= 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : filterColumnCount === 3
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : 'grid-cols-1 sm:grid-cols-2';

  return (
    <div className="w-full max-w-[700px] mx-auto relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-cloud-200/60 rounded-lg">
          <Loader />
        </div>
      )}
      <section className="p-4">
        <div className="p-6 border-b border-cloud-200 bg-cloud-50/30">
          <Text variant="text-base/leading-normal/medium" className="text-charcoal-1000">
            Mina annonser
          </Text>
          <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
            Sök, filtrera och öppna dina annonser och anbud.
          </Text>
        </div>

        <div className={cn('grid gap-3 mt-4', gridCols)}>
          <div>
            <Input
              tw
              name="ad-search"
              variant="fluid"
              type="text"
              label="Sök"
              value={searchText}
              onChange={handleSearchChange}
            />
          </div>
          <div>
            <Dropdown
            tw
              name="filter_year"
              label="Filtrera på år"
              description=""
              placeholder="Välj år"
              options={yearOptions}
              value={filterYear}
              variant="fluid"
              onChange={handleYearChange}
              onFocus={() => {}}
            />
          </div>
          {account.ads.length > 0 && (
            <div>
              <Dropdown
              tw
                name="filter_ads"
                label="Mina annonser"
                description=""
                placeholder="Välj kategori"
                options={categoryOptions}
                value={filterAds}
                variant="fluid"
                onChange={handleCategoryChange}
                onFocus={() => {}}
              />
            </div>
          )}
          {hasChildAccounts && (
            <div>
              <Dropdown
                tw
                name="filter_child_account"
                label="Anställda"
                description=""
                placeholder="Välj anställd"
                options={childAccountOptions}
                value={selectedChildId}
                variant="fluid"
                onChange={handleChildAccountChange}
                onFocus={() => {}}
              />
            </div>
          )}
        </div>

        <div className="mt-4">
          <Button variant="primary" fullWidth asChild>
            <a href="/salj-virke/" className="inline-flex items-center justify-center gap-2">
              <Icon tw name="PlusSimple" size="small" />
              Skapa annons
            </a>
          </Button>
        </div>

        <div className="mt-6 space-y-4">
          {userExpiringAds.length > 0 && (
            <CollapsibleSection
              title={`Tiden löper snart ut på dessa annonser (${userExpiringAds.length}/${account.ads.length}).`}
            >
              {ads.map((ad) =>
                userExpiringAds.includes(String(ad.id)) ? (
                  <AdCard
                    key={`exp-${ad.id}`}
                    ad={ad}
                    openSellTimberEditor={openSellTimberEditor}
                    onOpenHelpWithAdModal={(id) => setHelpWithAdModalAdId(id)}
                  />
                ) : null
              )}
            </CollapsibleSection>
          )}

          {account.ads.length > 0 && (
            <CollapsibleSection
              title={`Alla dina annonser (${ads.length}/${account.ads.length}).`}
            >
              {ads.map((ad) =>
                ad.show ? (
                  <AdCard
                    key={`mine-${ad.id}`}
                    ad={ad}
                    openSellTimberEditor={openSellTimberEditor}
                    onOpenHelpWithAdModal={(id) => setHelpWithAdModalAdId(id)}
                  />
                ) : null
              )}
            </CollapsibleSection>
          )}

          {account.bids.on_going.length > 0 && (
            <CollapsibleSection
              title={`Pågående anbud (${bids.on_going.length}/${account.bids.on_going.length}).`}
            >
              {bids.on_going.map((ad) =>
                ad.show ? (
                  <AdCard
                    key={`og-${ad.id}`}
                    ad={ad}
                    openSellTimberEditor={openSellTimberEditor}
                    onOpenHelpWithAdModal={(id) => setHelpWithAdModalAdId(id)}
                  />
                ) : null
              )}
            </CollapsibleSection>
          )}

          {account.bids.won.length > 0 && (
            <CollapsibleSection
              title={`Köpta annonser (${bids.won.length}/${account.bids.won.length}).`}
            >
              {bids.won.map((ad) =>
                ad.show ? (
                  <AdCard
                    key={`won-${ad.id}`}
                    ad={ad}
                    openSellTimberEditor={openSellTimberEditor}
                    onOpenHelpWithAdModal={(id) => setHelpWithAdModalAdId(id)}
                  />
                ) : null
              )}
            </CollapsibleSection>
          )}

          {account.bids.not_sold.length > 0 && (
            <CollapsibleSection
              title={`Annonser som inte blev sålda (${bids.not_sold.length}/${account.bids.not_sold.length}).`}
            >
              {bids.not_sold.map((ad) =>
                ad.show ? (
                  <AdCard
                    key={`ns-${ad.id}`}
                    ad={ad}
                    openSellTimberEditor={openSellTimberEditor}
                    onOpenHelpWithAdModal={(id) => setHelpWithAdModalAdId(id)}
                  />
                ) : null
              )}
            </CollapsibleSection>
          )}

          {account.bids.lost.length > 0 && (
            <CollapsibleSection title={`Förlorade anbud (${bids.lost.length}/${account.bids.lost.length}).`}>
              {bids.lost.map((ad) =>
                ad.show ? (
                  <AdCard
                    key={`lost-${ad.id}`}
                    ad={ad}
                    openSellTimberEditor={openSellTimberEditor}
                    onOpenHelpWithAdModal={(id) => setHelpWithAdModalAdId(id)}
                  />
                ) : null
              )}
            </CollapsibleSection>
          )}

          {account.opened.length > 0 && (
            <CollapsibleSection
              title={`Annonser du har öppnat (${opened.length}/${account.opened.length}).`}
            >
              {opened.map((ad) =>
                ad.show ? (
                  <AdCard
                    key={`op-${ad.id}`}
                    ad={ad}
                    openSellTimberEditor={openSellTimberEditor}
                    onOpenHelpWithAdModal={(id) => setHelpWithAdModalAdId(id)}
                  />
                ) : null
              )}
            </CollapsibleSection>
          )}
        </div>
      </section>

      <DynamicModal
        isOpen={helpWithAdModalAdId !== null}
        onClose={() => setHelpWithAdModalAdId(null)}
        className="bg-white-50 p-2"
        bp={{ sm: 'drawer', lg: 'dialog' }}
        dialogSize="large"
      >
        <DynamicModal.Header>
          <Text variant="text-lg/leading-normal/medium" className="text-charcoal-1000">
            Hjälp med annons
          </Text>
        </DynamicModal.Header>
        <DynamicModal.Content className="py-2 space-y-4">
          {helpWithAdSubmitSuccess ? (
            <div className="rounded-lg bg-success-100 border border-success-300 px-4 py-3 text-sm text-success-800">
              Tack! Vi har tagit emot ditt ärende och återkommer så snart vi kan.
            </div>
          ) : (
            <>
          <Dropdown
            tw
            name="help_with_ad_topic"
            label="Vad behöver du hjälp med"
            description=""
            placeholder="Välj alternativ"
            options={helpWithAdTopicDropdownOptions}
            value={helpWithAdTopic}
            variant="fluid"
            onChange={(value) => setHelpWithAdTopic(value)}
            onFocus={() => {}}
          />
          <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
            {HELP_WITH_AD_TOPIC_HINTS[helpWithAdTopic] ?? ''}
          </Text>

          <div className="space-y-2">
            <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
              Beskriv ditt ärende
            </Text>
            <Textarea
              tw
              className="min-h-28"
              name="help_with_ad_description"
              value={helpWithAdDescription}
              onChange={(value) => setHelpWithAdDescription(value)}
              placeholder="Skriv här…"
              rows={5}
            />
          </div>

          <FileUpload
            name="help_with_ad_attachments"
            label="Bilagor"
            buttonText="Välj eller dra filer hit"
            value={helpWithAdFiles}
            multiple
            onChange={(value, _name) =>
              setHelpWithAdFiles(value.map((f) => ({ ...f, valid: true })))
            }
            errorText="Storleksgräns: 10MB per fil"
            successText="Klart"
            idleText="Du kan bifoga flera filer."
          />

          {helpWithAdSubmitError ? (
            <Text variant="text-sm/leading-normal/normal" className="text-red-600">
              {helpWithAdSubmitError}
            </Text>
          ) : null}
            </>
          )}
        </DynamicModal.Content>
        <DynamicModal.Footer className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setHelpWithAdModalAdId(null)}>
            Stäng
          </Button>
          {!helpWithAdSubmitSuccess ? (
            <Button
              type="button"
              variant="primary"
              disabled={helpWithAdSubmitting || helpWithAdModalAdId === null}
              onClick={() => void submitHelpWithAdRequest()}
            >
              {helpWithAdSubmitting ? 'Skickar…' : 'Skicka'}
            </Button>
          ) : null}
        </DynamicModal.Footer>
      </DynamicModal>
    </div>
  );
}

function CollapsibleSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-cloud-500 bg-white-50 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-cloud-50/30 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
          {title}
        </Text>
        <Icon
          tw
          name="ChevronDownSimple"
          size="medium"
          className={cn('shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && <div className="px-4 py-3 space-y-3 bg-cloud-200/40">{children}</div>}
    </div>
  );
}

function AdCard({
  ad,
  openSellTimberEditor,
  onOpenHelpWithAdModal,
}: {
  ad: ListingAd;
  openSellTimberEditor: (id: number, e: React.MouseEvent) => void;
  onOpenHelpWithAdModal: (adId: number) => void;
}) {
  const title =
    `${ad.betekning ? `${ad.betekning} | ` : ''}${ad.muni && ad.muni !== 'None' ? `${ad.muni}, ` : ''}${ad.region ?? 'Utkast'}`;

  return (
    <div className="rounded-lg border border-cloud-200 bg-white-50 p-4 shadow-xs">
      <div className="flex flex-wrap gap-2">
        <div className="flex-1 min-w-[200px]">
          <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
            {title}
          </Text>
        </div>
        <Text variant="text-sm/leading-normal/medium" className="text-charcoal-600 shrink-0">
          #{ad.id}
        </Text>
      </div>
      <div className="mt-2 space-y-1">
        <Text variant="text-xs/leading-normal/normal" className="text-charcoal-600">
          {ad.total_volume ? (
            <span className="mr-2 pr-2 border-r border-green-spruce-600">
              {ad.total_volume} m3fub
            </span>
          ) : null}
          <span className="mr-2 pr-2 border-r border-green-spruce-600">{ad.type}</span>
          {ad.arrangement ?? ''}
        </Text>
        <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
          {ad.status}
        </Text>
      </div>

      {(ad.contract_pdf || ad.contact) && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {ad.contact && (
            <div>
              <Text variant="text-sm/leading-normal/medium" className="text-green-spruce-800">
                {ad.contact.label}
              </Text>
              <Text variant="text-xs/leading-normal/normal" className="text-charcoal-700 mt-1">
                <span className="font-medium">{ad.contact.name}</span>
                <br />
                Email:{' '}
                <a href={`mailto:${ad.contact.email}`} className="font-medium underline">
                  {ad.contact.email}
                </a>
                <br />
                Telefon:{' '}
                <a href={`tel:${ad.contact.phone}`} className="font-medium underline">
                  {ad.contact.phone}
                </a>
              </Text>
            </div>
          )}
          {ad.contract_pdf && (
            <div className="flex items-end">
              <Button variant="secondary" fullWidth asChild>
                <a href={ad.contract_pdf} target="_blank" rel="noopener noreferrer">
                  Ladda ner kontrakt
                </a>
              </Button>
            </div>
          )}
        </div>
      )}

      <AdCardActions
        ad={ad}
        openSellTimberEditor={openSellTimberEditor}
        onOpenHelpWithAdModal={onOpenHelpWithAdModal}
        fullfoljLabelStyle={ad.has_bids === true ? 'short' : 'long'}
      />
    </div>
  );
}

type AdCardActionItem = {
  key: string;
  label: string;
  href: string;
  external?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

function AdCardActions({
  ad,
  openSellTimberEditor,
  onOpenHelpWithAdModal,
  fullfoljLabelStyle,
}: {
  ad: ListingAd;
  openSellTimberEditor: (id: number, e: React.MouseEvent) => void;
  onOpenHelpWithAdModal: (adId: number) => void;
  fullfoljLabelStyle?: 'long' | 'short';
}) {
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

  const sellerLabel =
    ad.status === 'Fullfölj virkesannons'
      ? fullfoljLabelStyle === 'long'
        ? 'Fullfölj virkesannons'
        : 'Fullfölj annons'
      : 'Se annons';

  const actions: AdCardActionItem[] = [];

  if (ad.state === 15) {
    actions.push({
      key: 'seller',
      label: sellerLabel,
      href: `/min-skog/?view=sell-timber&sell-timber-ad=${ad.id}`,
      onClick: (e) => openSellTimberEditor(ad.id, e),
    });
  } else {
    actions.push({
      key: 'seller',
      label: sellerLabel,
      href: adUrlDetails(ad.id, 'ad'),
      external: true,
    });
  }

  if (ad.has_bids === true) {
    const bidLabel =
      ad.status === 'Du ska nu välja köpare' ? 'Välj köpare' : 'Se anbud';
    actions.push({
      key: 'bids',
      label: bidLabel,
      href: adUrlDetails(ad.id, 'bid'),
      external: true,
    });
  }

  if (ad.invoice_attachment_available) {
    actions.push({
      key: 'invoice',
      label: 'Ladda ner faktura',
      href: `/user-api/ad/${ad.id}/invoice-attachment/`,
      external: true,
    });
  }

  actions.push({
    key: 'help-with-ad',
    label: 'Hjälp med annons',
    href: '#',
    onClick: (e) => {
      e.preventDefault();
      onOpenHelpWithAdModal(ad.id);
    },
  });

  if (actions.length === 1) {
    const a = actions[0];
    return (
      <div className="mt-4">
        <Button variant="secondary" size="small" asChild>
          <a
            href={a.href}
            target={a.external ? '_blank' : undefined}
            rel={a.external ? 'noopener noreferrer' : undefined}
            onClick={a.onClick}
          >
            {a.label}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Popover
        open={actionsMenuOpen}
        onOpenChange={setActionsMenuOpen}
        side="bottom"
        align="end"
        contentClassName="min-w-[220px] p-2"
        className={cn(
          buttonVariants({ variant: 'secondary', size: 'small' }),
          'w-full sm:w-auto'
        )}
        content={
          <div className="flex flex-col gap-0.5">
            {actions.map((a) => (
              <a
                key={a.key}
                href={a.href}
                target={a.external ? '_blank' : undefined}
                rel={a.external ? 'noopener noreferrer' : undefined}
                onClick={(e) => {
                  a.onClick?.(e);
                  setActionsMenuOpen(false);
                }}
                className="rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-cloud-100"
              >
                <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
                  {a.label}
                </Text>
              </a>
            ))}
          </div>
        }
      >
        <span className="flex items-center justify-center gap-1">
          <Text variant="text-sm/leading-normal/medium">Alternativ</Text>
          <Icon tw name="ChevronDownSimple" size="small" />
        </span>
      </Popover>
    </div>
  );
}
